/**
 *
 * OktaVerifyStepOne Component
 *
 */

import React from "react";
import classnames from "classnames";
import {
  stylize,
  Button,
  Enums,
  Skeleton,
} from "@envestnet/envreact-component-library";
import withIntl from "utils/withIntl";
import messages from "./messages";
import styles, { Styles } from "./styles";
import { StatusType } from "./types";
import { AuthTypeEnum, StatusTypeEnum } from "./enums";

const { ButtonSize, ButtonStyle } = Enums;

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below
  onCancel: Function;

  onNext: Function;

  onCantScan: Function;

  loadQRCode: Function;

  qrCodeLink: string;

  status: StatusType;
}

export interface OktaVerifyStepOneProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;
  currentLayout: string;
}

const defaultProps: DefaultProps = {
  onCancel: () => {},
  onNext: () => {},
  onCantScan: () => {},
  loadQRCode: () => {},
  qrCodeLink: "",
  status: StatusTypeEnum.INITIAL,
};

type Props = InjectedProps & OktaVerifyStepOneProps;

const OktaVerifyStepOne = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    onCancel,
    onNext,
    onCantScan,
    loadQRCode,
    qrCodeLink,
    status,
    currentLayout,
  } = props;

  const [qrRendered, setQRRendered] = React.useState(false);

  React.useEffect(() => {
    loadQRCode({
      authenticatorType: AuthTypeEnum.OKTA,
      currentLayout,
    });
  }, []);

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div>
        <ol className={classes.orderedList}>
          <li className="header-text-font-family">
            {formatMessage(messages.listItem1)}
          </li>
          <li className="header-text-font-family">
            {formatMessage(messages.listItem2)}
          </li>
          <li className="header-text-font-family">
            {formatMessage(messages.listItem3)}
          </li>
          <li className="header-text-font-family">
            {formatMessage(messages.listItem4)}
          </li>
        </ol>

        <img
          src={qrCodeLink}
          alt={formatMessage(messages.loadingMessage)}
          loading="lazy"
          onLoad={() => {
            setQRRendered(true);
          }}
          style={
            qrRendered ? { visibility: "visible" } : { visibility: "hidden" }
          }
        ></img>

        {!qrRendered && (
          <Skeleton
            height={119}
            width={122}
            variant="rect"
            style={{ margin: "auto" }}
          />
        )}

        <div>
          <Button
            buttonStyle={ButtonStyle.Link}
            buttonSize={ButtonSize.Regular}
            className={classnames(
              classes.linkButton,
              "header-text-font-family"
            )}
            name={formatMessage(messages.cantScanLabel)}
            disabled={!qrRendered}
            onClick={() => {
              onCantScan();
            }}
            aria-label="challenge question submit button"
          />
        </div>
        <div>
          <Button
            buttonStyle={ButtonStyle.Primary}
            buttonSize={ButtonSize.Regular}
            className={classnames(classes.nextButton, "no-border-radius", "header-text-font-family")}
            name={formatMessage(messages.nextButtonLabel)}
            disabled={!qrRendered}
            loading={status === StatusTypeEnum.PROCESSING || !qrRendered}
            onClick={() => {
              onNext();
            }}
            aria-label="challenge question submit button"
          />
        </div>

        <div>
          <Button
            buttonStyle={ButtonStyle.Link}
            buttonSize={ButtonSize.Regular}
            className={classnames(
              classes.linkButton,
              "header-text-font-family"
            )}
            name={formatMessage(messages.cancelButtonLabel)}
            disabled={!qrRendered}
            onClick={() => {
              onCancel();
            }}
            aria-label="challenge question submit button"
          />
        </div>
      </div>
    </div>
  );
};

OktaVerifyStepOne.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(OktaVerifyStepOne)
);
